import React, { type ComponentType, useCallback, type Ref, useRef, useMemo } from 'react';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import Button, { type ButtonProps } from '@atlaskit/button';
import AddIcon from '@atlaskit/icon/core/migration/add';
import { Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import {
	type FieldOption,
	MetaActions,
	type SelectOption,
	type OnChangeWithData,
	type EventType,
	type KeyboardEventType,
} from '@atlassian/jira-jql-builder-basic-picker/src/common/types.tsx';
import type { PickerProps } from '@atlassian/jira-jql-builder-basic-picker/src/ui/types.tsx';
import useMergeRefs from '@atlassian/jira-merge-refs/src/index.tsx';
import Placeholder from '@atlassian/jira-placeholder/src/index.tsx';
import { FireUiAnalytics } from '@atlassian/jira-product-analytics-bridge';
import type {
	JiraJqlViewContext,
	JiraJqlScopeInput,
} from '@atlassian/jira-relay/src/__generated__/useFieldsQuery.graphql';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';
import { lazyForPaint } from '@atlassian/react-loosely-lazy';
import PickerSync from '@atlassian/jira-jql-builder-basic-picker/src/index.tsx';
import commonMessages from '../../../common/messages.tsx';
import type {
	AddFilterButtonComponent,
	FieldToRender,
	NoOptionComponentType,
} from '../../../common/types.tsx';
import { useActivePopupKey } from '../../../controllers/active-popup/index.tsx';
import { useTextSearchInputClause, useAST } from '../../../controllers/ast/index.tsx';
import { getPickerType } from '../../../controllers/picker-type/index.tsx';
import { useFields } from '../../../services/use-fields/useFields.tsx';
import { removeFieldsFromJql } from '../../../utils/remove-fields-from-jql/index.tsx';
import { useSendAnalytics } from '../../../utils/use-send-analytics.tsx';
import { ADD_FILTER_DROPDOWN } from '../../../common/constants.tsx';
import messages from './messages.tsx';

// @deprecated render_jql_builder_in_ssr will load JQL Builder Picker sync
// eslint-disable-next-line jira/deprecations/no-rll-client-async-experiences
export const PickerAsync = lazyForPaint<ComponentType<PickerProps>>(
	() =>
		import(
			/* webpackChunkName: "async-jql-builder-basic-picker" */ '@atlassian/jira-jql-builder-basic-picker'
		),
	{ ssr: false },
);

type TriggerWrapperProps = ButtonProps & {
	triggerRef: Ref<HTMLDivElement>;
	popupRef: Ref<HTMLElement>;
	ButtonComponent: AddFilterButtonComponent;
};

const TriggerButtonWithMergeRef = ({
	triggerRef,
	popupRef,
	ButtonComponent,
	...props
}: TriggerWrapperProps) => {
	const ref = useMergeRefs(triggerRef, popupRef);

	return <ButtonComponent ref={ref} {...props} />;
};

export type OnAddFilterChange = (props: {
	fieldsToRender: FieldToRender[];
	selectedFields: FieldOption[];
}) => void;

type AddFilterProps = {
	value: SelectOption[];
	fieldsToRender: FieldToRender[];
	selectedFields: FieldOption[];
	/**
	 * List of fields (JQL terms) that are to be omitted from the select options
	 */
	excludeFields: Set<string>;
	onChange: OnAddFilterChange;
	onSearchWrapper?: (jql: string) => void;
	onClearValues?: () => void;
	NoOptionComponent?: NoOptionComponentType;
	ButtonComponent?: AddFilterButtonComponent;
	contextJql: string;
	viewContext: JiraJqlViewContext | null;
	scope: JiraJqlScopeInput | null;
};

const isKeyboardEvent = (event: EventType): event is KeyboardEventType =>
	Object.hasOwn(event, 'key');

export const AddFilter = ({
	value,
	fieldsToRender,
	selectedFields,
	onSearchWrapper,
	onClearValues,
	NoOptionComponent,
	onChange,
	excludeFields,
	contextJql,
	viewContext,
	scope,
	ButtonComponent = Button,
}: AddFilterProps) => {
	const { formatMessage } = useIntl();
	const [activePopupKey, { openPopup, closePopup }] = useActivePopupKey();
	const onOpen = useCallback(() => openPopup(ADD_FILTER_DROPDOWN), [openPopup]);
	const onClose = useCallback(() => closePopup(ADD_FILTER_DROPDOWN), [closePopup]);
	const [ast] = useAST();
	const [textSearchInputClause] = useTextSearchInputClause();

	const excludedFields = useMemo<string[]>(() => Array.from(excludeFields), [excludeFields]);

	const onLoadOptions = useFields({
		jql: contextJql,
		excludeFields: excludedFields,
		viewContext,
		scope,
	});

	const isOpen = activePopupKey === ADD_FILTER_DROPDOWN;

	const sendAnalytics = useSendAnalytics();
	const buttonRef = useRef<HTMLDivElement>(null);

	const iconAfter = useMemo(
		() =>
			isVisualRefreshEnabled() ? (
				<ChevronDownIcon
					label=""
					color={
						// eslint-disable-next-line no-nested-ternary
						fg('visual-refresh-headers-buttons')
							? !isOpen
								? token('color.icon.subtle')
								: 'currentColor'
							: 'currentColor'
					}
					size="small"
				/>
			) : (
				<AddIcon label="" LEGACY_size="small" />
			),
		[isOpen],
	);

	const buttonLabel = useMemo(() => {
		// eslint-disable-next-line no-nested-ternary
		return isVisualRefreshEnabled() ? (
			fg('visual-refresh-headers-buttons') ? (
				<Text weight="medium" color={!isOpen ? 'color.text.subtle' : 'inherit'}>
					{formatMessage(commonMessages.moreButton)}
				</Text>
			) : (
				<>{formatMessage(commonMessages.moreButton)}</>
			)
		) : (
			<>{formatMessage(commonMessages.moreButtonOld)}</>
		);
	}, [isOpen, formatMessage]);

	const loadingMessages = useMemo(() => {
		return fg('jql_basic_picker_infinite_scroll_loading_fix')
			? {
					paginationStart: formatMessage(messages.loadingMessageStart),
					paginationEnd: formatMessage(messages.loadingMessageEnd),
				}
			: undefined;
	}, [formatMessage]);

	const handleClose = useCallback(
		(
			event:
				| Event
				| React.MouseEvent<HTMLElement>
				| KeyboardEvent
				| React.KeyboardEvent<HTMLDivElement>,
		) => {
			onClose();
			if (isKeyboardEvent(event) && event.key === 'Escape') {
				buttonRef.current?.focus();
			}
		},
		[onClose],
	);

	const CustomNoOptionsMessage = useCallback(
		() =>
			NoOptionComponent ? (
				<>
					<NoOptionComponent onClickLink={onClose} sourceId="moreFieldsPicker" />
					<FireUiAnalytics
						actionSubject="unsupportedFieldsMessage"
						action="viewed"
						actionSubjectId="moreFieldsPicker"
					/>
				</>
			) : undefined,
		[NoOptionComponent, onClose],
	);

	const onTrigger = useCallback(
		({ ref }: { ref: Ref<HTMLElement> }) => {
			return (
				<TriggerButtonWithMergeRef
					triggerRef={buttonRef}
					popupRef={ref}
					appearance="default"
					iconAfter={iconAfter}
					isSelected={isOpen}
					aria-expanded={isOpen}
					onClick={isOpen ? onClose : onOpen}
					label={formatMessage(commonMessages.moreButtonLabel)}
					aria-label={formatMessage(commonMessages.moreButtonLabel)}
					testId="jql-builder-basic.ui.jql-editor.add-filter.more-button"
					ButtonComponent={ButtonComponent}
				>
					{buttonLabel}
				</TriggerButtonWithMergeRef>
			);
		},
		[iconAfter, isOpen, onClose, onOpen, formatMessage, ButtonComponent, buttonLabel],
	);

	const handleChange: OnChangeWithData = useCallback(
		(options, meta) => {
			switch (meta.action) {
				case MetaActions.selectOption:
					if ('option' in meta && meta.option?.optionType === 'field') {
						const newSelectedFields = [...selectedFields, meta.option];
						const picker = getPickerType({
							fieldType: meta.option.fieldType,
							searchTemplate: meta.option.searchTemplate,
							operators: meta.option.operators,
						});
						const newFieldsToRender: FieldToRender[] = [
							...fieldsToRender,
							{
								fieldId: meta.option.value,
								fieldLabel: meta.option.label,
								picker,
								clause: undefined,
								secondaryClause: undefined,
								fieldDescription: meta.option.fieldDescription,
								fieldTypeDisplayName: meta.option.fieldTypeDisplayName,
							},
						];
						onChange({
							selectedFields: newSelectedFields,
							fieldsToRender: newFieldsToRender,
						});
						openPopup(meta.option.value);
						sendAnalytics({
							actionSubjectId: 'addFilter',
							attributes: {
								fieldType: picker.fieldType,
								searchTemplate: picker.searchTemplate,
								refinementType: picker.pickerType,
								propType: picker.propType,
							},
						});
					}
					break;
				case MetaActions.deselectOption:
					if ('option' in meta && meta.option?.optionType === 'field' && !meta.option.isDisabled) {
						const newSelectedFields = selectedFields.filter(
							(activeField) =>
								meta.option?.optionType === 'field' && meta.option.value !== activeField.value,
						);
						const newFieldsToRender = fieldsToRender.filter((fieldToRender) => {
							if (
								meta.option?.optionType === 'field' &&
								meta.option?.value === fieldToRender.fieldId
							) {
								onSearchWrapper &&
									onSearchWrapper(
										removeFieldsFromJql(
											ast,
											new Set([fieldToRender.fieldId]),
											textSearchInputClause,
										),
									);
								return !!(fieldToRender.secondaryClause || fieldToRender.clause);
							}
							return true;
						});
						onChange({
							selectedFields: newSelectedFields,
							fieldsToRender: newFieldsToRender,
						});
						const picker = getPickerType({
							fieldType: meta.option.fieldType,
							searchTemplate: meta.option.searchTemplate,
							operators: meta.option.operators,
						});
						sendAnalytics({
							actionSubjectId: 'removeFilter',
							attributes: {
								fieldType: picker.fieldType,
								searchTemplate: picker.searchTemplate,
								refinementType: picker.pickerType,
								propType: picker.propType,
								isOnlyUnselectable: meta.option.isOnlyUnselectable ?? false,
							},
						});
					}
					break;
				default:
			}
		},
		[
			ast,
			fieldsToRender,
			onChange,
			onSearchWrapper,
			openPopup,
			textSearchInputClause,
			selectedFields,
			sendAnalytics,
		],
	);

	const TriggerButton = ButtonComponent ?? Button;

	return (
		<>
			{fg('render_jql_builder_in_ssr') ? (
				<PickerSync
					isOpen={isOpen}
					onClose={handleClose}
					value={value}
					id={ADD_FILTER_DROPDOWN}
					onChange={handleChange}
					onLoadOptions={onLoadOptions}
					enableFullList={false}
					onTrigger={onTrigger}
					onClearValues={onClearValues}
					CustomNoOptionsMessage={CustomNoOptionsMessage}
					shouldRenderToParent
					searchInputPlaceholder={formatMessage(commonMessages.searchMoreFiltersPlaceholder)}
					loadingMessages={loadingMessages}
				/>
			) : (
				<Placeholder
					name="add-filter-picker"
					fallback={<TriggerButton iconAfter={iconAfter}>{buttonLabel}</TriggerButton>}
				>
					<PickerAsync
						isOpen={isOpen}
						onClose={handleClose}
						value={value}
						id={ADD_FILTER_DROPDOWN}
						onChange={handleChange}
						onLoadOptions={onLoadOptions}
						enableFullList={false}
						onTrigger={onTrigger}
						onClearValues={onClearValues}
						CustomNoOptionsMessage={CustomNoOptionsMessage}
						shouldRenderToParent
						searchInputPlaceholder={formatMessage(commonMessages.searchMoreFiltersPlaceholder)}
						loadingMessages={loadingMessages}
					/>
				</Placeholder>
			)}
		</>
	);
};

export default AddFilter;
